import { useState, useEffect, useRef, useCallback } from 'react';

// Invoice templates are designed for A4 (210mm ≈ 794px at 96dpi)
const A4_WIDTH_PX = 794;

export const InvoicePreview = (props) => {
	const [invoiceHtml, setInvoiceHtml] = useState("");
	const [state, setstate] = useState(2);
	const [fitToScreen, setFitToScreen] = useState(true);
	const [containerWidth, setContainerWidth] = useState(A4_WIDTH_PX);
	const [contentHeight, setContentHeight] = useState(1123); // A4 height until the invoice is measured

	const containerRef = useRef(null);
	const iframeRef = useRef(null);

	var page_name = "invoice-preview";

	async function getCustomerInvoiceHtmlDetails() {
		const response = await fetch(
			`${process.env.REACT_APP_SERVER_URL}/get-customer-invoice-html-details`, { method: 'POST', body: JSON.stringify({ "type": "html_id", "value": props.match.params.id, "page_name": page_name }), headers: { "Content-type": "application/json; charset=UTF-8" } }
		);
		const body = await response.json();

		if (body.operation === "success" && body.data && body.data.html_template) {
			setInvoiceHtml(body.data.html_template);
		}
		else {
			throw new Error(body.message, { cause: "api error" })
		}
	}

	useEffect(() => {
		getCustomerInvoiceHtmlDetails()
			.then(() => {
				setstate(1);
			})
			.catch((err) => {
				setstate(0);
				console.log(err)
			})
	}, [])

	// available width for the invoice (changes on rotate / resize)
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const updateWidth = () => setContainerWidth(container.clientWidth);
		updateWidth();

		const observer = new ResizeObserver(updateWidth);
		observer.observe(container);
		return () => observer.disconnect();
	}, [state]);

	// height of the invoice document inside the iframe (re-measured when images / fonts change its size)
	const measureContent = useCallback(() => {
		const doc = iframeRef.current?.contentDocument;
		if (!doc || !doc.documentElement) return;
		const height = Math.max(doc.documentElement.scrollHeight, doc.body ? doc.body.scrollHeight : 0);
		if (height > 0) setContentHeight(height);
	}, []);

	const handleIframeLoad = () => {
		measureContent();
		const doc = iframeRef.current?.contentDocument;
		if (doc && doc.body && window.ResizeObserver) {
			const observer = new ResizeObserver(measureContent);
			observer.observe(doc.body);
			iframeRef.current._resizeObserver = observer;
		}
	};

	useEffect(() => () => iframeRef.current?._resizeObserver?.disconnect(), []);

	// print the invoice document itself so its own A4 print styles are used
	const handleDownload = () => {
		const frameWindow = iframeRef.current?.contentWindow;
		try {
			frameWindow.focus();
			frameWindow.print();
		}
		catch (err) {
			// fallback: open the invoice in a new window and print from there
			const printWindow = window.open('', '_blank');
			if (!printWindow) return;
			printWindow.document.open();
			printWindow.document.write(invoiceHtml);
			printWindow.document.close();
			printWindow.onload = () => printWindow.print();
		}
	};

	const scale = fitToScreen ? Math.min(1, containerWidth / A4_WIDTH_PX) : 1;
	const isScaled = scale < 1;

	return (
		<>
			<style>
				{`
				.ip-page {
					background: #eef1f6;
					min-height: 100vh;
				}

				.ip-header {
					position: sticky;
					top: 0;
					z-index: 10;
					display: flex;
					flex-wrap: wrap;
					justify-content: space-between;
					align-items: center;
					gap: 12px;
					padding: 12px 20px;
					background: #fff;
					box-shadow: 0 1px 6px rgba(0,0,0,0.08);
				}

				.ip-title {
					margin: 0;
					font-size: 20px;
					font-weight: 700;
					color: #1f2937;
				}

				.ip-actions {
					display: flex;
					flex-wrap: wrap;
					align-items: center;
					gap: 8px;
				}

				.ip-zoom {
					display: inline-flex;
					border: 1px solid #d0d5dd;
					border-radius: 6px;
					overflow: hidden;
				}

				.ip-zoom button {
					border: 0;
					background: #fff;
					padding: 6px 12px;
					font-size: 13px;
					font-weight: 600;
					color: #475467;
				}

				.ip-zoom button.active {
					background: #e8f1ff;
					color: #1b6ef3;
				}

				.ip-body {
					padding: 20px;
				}

				.ip-container {
					width: 100%;
					max-width: ${A4_WIDTH_PX}px;
					margin: 0 auto;
				}

				.ip-container.ip-zoomed {
					max-width: none;
					overflow-x: auto;
					-webkit-overflow-scrolling: touch;
				}

				.ip-sheet {
					position: relative;
					margin: 0 auto;
					background: #fff;
					box-shadow: 0 2px 14px rgba(0,0,0,0.10);
					border-radius: 4px;
					overflow: hidden;
				}

				.ip-sheet iframe {
					position: absolute;
					top: 0;
					left: 0;
					border: 0;
					transform-origin: 0 0;
					background: #fff;
				}

				.ip-hint {
					text-align: center;
					font-size: 12px;
					color: #667085;
					margin-top: 10px;
				}

				.ip-status {
					text-align: center;
					padding: 60px 16px;
					color: #475467;
				}

				@media (max-width: 576px) {
					.ip-header {
						padding: 10px 12px;
					}

					.ip-title {
						font-size: 16px;
						width: 100%;
					}

					.ip-actions {
						width: 100%;
					}

					.ip-actions .btn {
						flex: 1 1 auto;
						padding: 8px 10px;
						font-size: 13px;
					}

					.ip-body {
						padding: 10px 8px;
					}
				}
				`}
			</style>

			<div className="ip-page">
				<div className="ip-header">
					<h1 className="ip-title">View Customer Invoice</h1>

					<div className="ip-actions">
						{state === 1 && (
							<div className="ip-zoom" role="group" aria-label="Zoom">
								<button type="button" className={fitToScreen ? "active" : ""} onClick={() => setFitToScreen(true)}>Fit</button>
								<button type="button" className={!fitToScreen ? "active" : ""} onClick={() => setFitToScreen(false)}>100%</button>
							</div>
						)}

						<button className="btn btn-primary" onClick={handleDownload} disabled={state !== 1}>
							Download Invoice
						</button>

						<a
							href="https://www.spoterp.com"
							target="_blank"
							rel="noreferrer"
							className="btn btn-light"
						>
							Try SpotERP
						</a>
					</div>
				</div>

				<div className="ip-body">
					{state === 2 && (
						<div className="ip-status">
							<span className="spinner-border text-primary" role="status"></span>
							<div className="mt-3">Loading invoice…</div>
						</div>
					)}

					{state === 0 && (
						<div className="ip-status">
							<h3 className="fw-bold">Invoice not available</h3>
							<div>This invoice link is invalid or has expired. Please contact the sender.</div>
						</div>
					)}

					{state === 1 && (
						<>
							<div ref={containerRef} className={`ip-container ${!fitToScreen ? "ip-zoomed" : ""}`}>
								{/* sheet keeps the scaled size so the page scrolls correctly */}
								<div className="ip-sheet" style={{ width: A4_WIDTH_PX * scale, height: contentHeight * scale }}>
									{/* sandbox: invoice html is isolated, its scripts never run */}
									<iframe
										ref={iframeRef}
										title="Customer Invoice"
										srcDoc={invoiceHtml}
										sandbox="allow-same-origin allow-modals"
										onLoad={handleIframeLoad}
										scrolling="no"
										style={{ width: A4_WIDTH_PX, height: contentHeight, transform: `scale(${scale})` }}
									/>
								</div>
							</div>
							{isScaled && (
								<div className="ip-hint">Tap “100%” to view the invoice in full size</div>
							)}
						</>
					)}
				</div>
			</div>
		</>);

};

export default InvoicePreview;
